import { createSelector } from 'reselect'
import Cesium from 'cesiumDll/Cesium'
import { voronoi } from 'd3-voronoi'

import {
  earthRadiusKm,
  getAngle,
  getDistance,
  lng2tileX,
  lat2tileY,
} from '../lib/helpers'

const getMode = (state) => state.map.mode

const getBounds = (state) => state.map.currentBounds

const getBoundsRound = createSelector(
  [ getBounds ], 
  ( bounds ) => {

    if ( ! bounds) {
      return {
        west: -180,
        east: 180,
        north: 90,
        south: -90,
      }
    }

    return {
      west: Math.max(-180, Math.floor(bounds.west)-.5),
      east: Math.min(180, Math.ceil(bounds.east)+.5),
      north: Math.min(90, Math.ceil(bounds.north)+.5),
      south: Math.max(-90, Math.floor(bounds.south)-.5),
    }
  }
)

const getMagnitude = (state) => state.map.currentMagnitude

const batteryEntities = (state) => state.entities.batteries

export const getBatteries = createSelector(
  [ batteryEntities, getMode ],
  ( batteryEntities, mode ) => {

    if (mode !== 'live') {
      return {}
    }

    // console.log("getBatteries",Object.keys(batteryEntities).length)

    let batteries = {}
    let batteryIds = {}
    let batteryIndex = 0
    // load all initial api battery pages first
    if ( ! batteryEntities || window.api__batteriesCurrentPage < window.api__batteriesTotalPages) {
      return {}
    }
    // console.log("batteries",Object.keys(batteryEntities).length)
    return batteryEntities

    // let locations = {}
    // let len = Object.keys(batteryEntities).length
    // let keys = Object.keys(batteryEntities)

    // for (let i=0; i<len; i++) {
    //   if (batteries.length <= batteryIndex) {
    //     return
    //   }

    //   let battery = batteryEntities[keys[batteryIndex]]

    //   if ( ! battery) {
    //     // console.log("no location")
    //     batteryIndex++
    //     continue
    //   }

    //   if (batteryIds[battery.id] === true) {
    //     // console.log("id exists",battery.id)
    //     batteryIndex++
    //     continue
    //   }

    //   let location = battery.lng+'|'+battery.lat
    //   // console.log(locations.indexOf(location))
    //   if (locations[location] && locations[location] >= 1) {
    //     // console.log("location already exists",location)
    //     // batteryIndex++
    //     // continue
    //     locations[location] += 1
    //     battery = spreadLatLng(battery, locations[location])
    //   } else {
    //     locations[location] = 1
    //   }

    //   // batteries[battery.id] = spreadLatLng(battery)
    //   batteries[battery.id] = battery
    //   // if (battery.lat === -33.7927 || battery.lng === 151.242 || [12240, 32668, 34220].indexOf(battery.id) >= 0) {
    //   //   console.log(battery)
    //   // }
    //   if (batteries[battery.id].hasOwnProperty('battery')) {
    //     // console.log(battery)
    //     delete batteries[battery.id]['battery']
    //   }
    //   batteryIds[battery.id] = true
      
    //   batteryIndex++
    // }
    // return batteries
  }
)

export const getBatteriesByBounds = createSelector(
  [ getBatteries, getBoundsRound ],
  ( batteries, bounds ) => {
    // console.log(bounds)

    let batteriesByBounds = Object.keys(batteries).reduce((acc, batteryId, index) => {
      let battery = batteries[batteryId]
      if (battery.lng >= bounds.west 
        && battery.lng <= bounds.east 
        && battery.lat >= bounds.south  
        && battery.lat <= bounds.north
      ) {
        acc[batteryId] = battery
      }
      return acc
    }, {})

    // console.log('batteriesByBounds', Object.keys(batteriesByBounds).length)

    return batteriesByBounds
  }
)

export const getWeightBounds = createSelector(
  [ getBatteriesByBounds ],
  ( batteries ) => {

    let w = null
    let s = null
    let e = null
    let n = null

    let batteriesWithWeight = Object.keys(batteries).reduce((acc, batteryId, index) => {
      let battery = batteries[batteryId]
      if (battery.weight && battery.weight > 1) {
        acc[batteryId] = battery
        
        if ( ! w || battery.lng < w) {
          w = Math.floor(battery.lng)
        }
        if ( ! s || battery.lat < s) {
          s = Math.floor(battery.lat)
        }
        if ( ! e || battery.lng > e) {
          e = Math.ceil(battery.lng)
        }
        if ( ! n || battery.lat > n) {
          n = Math.ceil(battery.lat)
        }
      }
      return acc
    }, {})

    let deltaLat = n-s
    let deltaLng = e-w
    // console.log(deltaLat,deltaLng)
    if (Object.keys(batteriesWithWeight).length > 0 
      && deltaLat <= 5 
      && deltaLng <= 5
    ) {
      return new Cesium.Rectangle(w, s, e, n)
    }
    return null
  }
)

export const getBatteriesNet = createSelector(
  [ getBatteriesByBounds, getBoundsRound ],
  ( batteries, bounds ) => {

    switch (true) {
      case bounds.north-bounds.south > 5 :
      case bounds.east-bounds.west > 5 :
        return []
    }

    const R = earthRadiusKm
    let v = voronoi()
    let edges = []

    for (let key of Object.keys(batteries)) {
      edges.push([batteries[key].lat, batteries[key].lng])
    }

    // console.log("edges", edges.length)

    let links = v.links(edges)
    // console.log("links", links.length)
    // return links

    let shortLinks = []
    // console.log('voronoi',links.length)
    for (let l=0; l<links.length; l++) {
      let link = links[l]
      let length = getDistance(link.source[1], link.source[0], link.target[1], link.target[0])
      if (length <= 100) {
        link.length = length
        shortLinks.push(link)
      }
    }
    // console.log("short links",shortLinks.length)
    shortLinks.sort((a,b) => {
      if (a.length < b.length) {
        return -1;
      }
      if (a.length > b.length) {
        return 1;
      }
      // a must be equal to b
      return 0;
    })
    // console.log('sorted links')
    // console.log("shortLinks",shortLinks.length)
    return shortLinks
  }
)

export const getBatteriesNetByBounds = createSelector(
  [ getBatteriesNet, getBoundsRound, getMagnitude ],
  ( links, bounds, magnitude ) => {
    // console.log("getBatteriesNetByBounds", links.length)
    
    // let linksByBounds = []
    // if (Math.abs(magnitude-earthRadiusKm) > 50) {
    //   return linksByBounds
    // }
    // let deltaLat = bounds.east-bounds.west
    // let deltaLng = bounds.north-bounds.south
    // // console.log(deltaLat,deltaLng)
    // if (deltaLat > 1 || deltaLng > 1) {
    //   return linksByBounds
    // }

    // for (let l=0; l<links.length; l++) {
    //   let link = links[l]
    //   switch (true) {
    //     // source is in bounds
    //     case link.source[1] > bounds.west && link.source[1] < bounds.east && link.source[0] > bounds.south && link.source[0] < bounds.north :
    //       linksByBounds.push(link)
    //       break

    //     // target is in bounds
    //     case link.target[1] > bounds.west && link.target[1] < bounds.east && link.target[0] > bounds.south && link.target[0] < bounds.north :
    //       linksByBounds.push(link)
    //       break
    //   }
    // }

    let batteriesNet = []
    let positions = {}
    // for (let link of linksByBounds) {
    for (let link of links) {

      let sourceKey = link.source[0]+','+link.source[1]
      if (positions[sourceKey] === undefined) {
        positions[sourceKey] = []
      }

      let skip = false
      for (let vKey of Object.keys(positions[sourceKey])) {
        let v = positions[sourceKey][vKey]
        let deg = Cesium.Math.toDegrees(getAngle(
          {x: v[0], y: v[1]},
          {x: link.source[0], y: link.source[1]},
          {x: link.target[0], y: link.target[1]}
        ))
        // if (isNaN(deg)) { console.log({v,link}) }
        if (deg < 45) {
          skip = true
          continue
        }
      }

      if (skip) {
        // console.log("skip")
        continue
      }

      let targetKey = link.target[0]+','+link.target[1]
      if (positions[targetKey] === undefined) {
        positions[targetKey] = []
      }

      for (let vKey of Object.keys(positions[targetKey])) {
        let v = positions[targetKey][vKey]
        let deg = Cesium.Math.toDegrees(getAngle(
          {x: v[0], y: v[1]},
          {x: link.target[0], y: link.target[1]},
          {x: link.source[0], y: link.source[1]}
        ))
        // if (isNaN(deg)) { console.log({v,link}) }
        if (deg < 45) {
          skip = true
          continue
        }
      }

      if (skip) {
        // console.log("skip")
        continue
      }

      positions[sourceKey].push(link.target)
      positions[targetKey].push(link.source)

      batteriesNet.push(link)
    }
    // console.log('sort out')


    // console.log("getBatteriesNetByBounds",batteriesNet.length)
    return batteriesNet
  }
)

export const getBatteriesByTiles = createSelector(
  [ getBatteries ],
  ( batteries) => {
    let batteriesByTiles = {}

    for (let id in batteries) {

      let battery = batteries[id]

      let tileX = lng2tileX(battery.lng)
      let tileY = lat2tileY(battery.lat)
      let tileKey = '_'+tileX+'_'+tileY
// console.log(tileKey)
      if (typeof(batteriesByTiles[tileKey]) == "undefined") {
        let tile = {batteries: [battery]}
        batteriesByTiles[tileKey] = tile
      } else {
        batteriesByTiles[tileKey].batteries.push(battery)
      }
    }
// console.log(batteriesByTiles)
// console.log("COMPLETE getBatteriesByTiles",Object.keys(batteriesByTiles).length)
// console.log(batteriesByTiles)
    return batteriesByTiles
  }
)

const selectedEntity = (state) => state.entities.selected

export const getSelectedEntity = createSelector(
  [ selectedEntity ],
  ( selectedEntity ) => {
    return spreadLatLng(selectedEntity)
  }
)

const videoEntities = (state) => state.entities.videos

export const getVideos = createSelector(
  [ videoEntities, getMode ],
  ( videoEntities, mode ) => {
    if (mode !== 'stories') {
      return {}
    }

    let videos = {}
    for (let key of Object.keys(videoEntities)) {
      let video = videoEntities[key]
      videos[video.id] = spreadLatLng(video)
    }
    return videos
  }
)


const ambassadorEntities = (state) => state.entities.ambassadors

export const getAmbassadors = createSelector(
  [ ambassadorEntities, getMode ],
  ( ambassadorEntities, mode ) => {
    if (mode !== 'ambassadors') {
      return {}
    }

    let ambassadors = {}
    for (let key of Object.keys(ambassadorEntities)) {
      let ambassador = ambassadorEntities[key]
      ambassadors[ambassador.id] = spreadLatLng(ambassador, 0, 0)
    }
    return ambassadors
  }
)


const awardEntities = (state) => state.entities.awards

export const getAwards = createSelector(
  [ awardEntities ],
  ( awardEntities ) => {
    let awards = {}
    for (let key of Object.keys(awardEntities)) {
      let award = awardEntities[key]
      awards[award.id] = spreadLatLng(award)
    }
    return awards
  }
)

const spreadLatLng = (object, offset = 0, multiplier = 1) => {

  let r = 100000

  if (object && object.lat && object.lng) {

    let latRemainder = (object.lat*r)%Math.floor(object.lat*r)
    let lngRemainder = (object.lng*r)%Math.floor(object.lng*r)

    if (latRemainder == 0 || lngRemainder == 0) {
      let lat = object.lat
      let lng = object.lng

      let angle = Math.sqrt(offset) * 887 // object.id

      let x = angle*Math.cos(angle)
      let y = angle*Math.sin(angle)

      lat += y/1000000 * multiplier
      lng += x/1000000 * multiplier

      object.lat = lat // +offset
      object.lng = lng // +offset
    }
  }

  return object
}






// WEBPACK FOOTER //
// ./src/selectors/index.js